/**
 * Manages global announcements
 *
 * Copyright (c) 2023 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import React, { useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import Cookies from 'js-cookie'
import { Badge } from 'reactstrap'
import Banner from '../Banner'
import Link from '../Link'
import { PRODUCT, WHATS_NEW } from './enums'
import styles from './AnnouncementsBanner.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const ANNOUNCEMENT_COOKIE_ID = `announcement`

// Only show "whats new" when signed in. These items are dssmissable
const checkIfTagExists = (tags, providedTag) => (
  tags.findIndex(({ slug }) => slug === providedTag?.id) > -1
)

const AnnouncementsBanner = ({
  announcements = [],
  allowedTags = [],
  disableDismiss = false
}) => {
  // Determine the latest announcement to show
  const activeAnnouncement = useMemo(() => {
    return announcements.find(({ id, tags }) => {
      if (Cookies.get(`${ANNOUNCEMENT_COOKIE_ID}_${id}`)) {
        // If the cookie exists, the user has already dismised it, so hide it
        return false
      } else if (allowedTags.length > 0) {
      // If allowedTags is provided, we only want to show announcements that have that tag
        return allowedTags.findIndex((tag) => checkIfTagExists(tags, tag)) > -1
      } else {
        return true
      }
    })
  }, [announcements, allowedTags])

  const activeTag = useMemo(() => {
    const tags = activeAnnouncement?.tags || []
    if (checkIfTagExists(tags, WHATS_NEW.id)) {
      return WHATS_NEW
    }

    return PRODUCT
  }, [activeAnnouncement])

  const handleDismiss = useCallback(() => {
    // Expires every 30 days because the announcement won't appear after that
    Cookies.set(`${ANNOUNCEMENT_COOKIE_ID}_${activeAnnouncement.id}`, true, { expires: 30 })
  }, [activeAnnouncement])

  return activeAnnouncement ? (
    <Banner
      alertClass={cx(styles['announcement-alert-wrapper'])}
      className={cx(styles['announcement-banner'])}
      color={activeTag.color}
      onDismiss={!disableDismiss ? handleDismiss : null}>
      <div className={cx(styles['banner-content'])}>
        <Badge className={cx(styles['tag-badge'])}
          color={activeTag.color}>
          {activeTag.title}
        </Badge>
        <span>
          {activeAnnouncement.excerpt}{' '}
          <Link to={activeAnnouncement.url} anchor isNewTab>
            Learn more <FontAwesomeIcon icon={['fas', 'up-right-from-square']} className='font-xxs align-top' />
          </Link>
        </span>
      </div>
    </Banner>
  ) : null
}

AnnouncementsBanner.propTypes = {
  announcements: PropTypes.array,
  allowedTags: PropTypes.array,
  disableDismiss: PropTypes.bool
}

export default AnnouncementsBanner
